import { z } from 'zod';
import { DateTime } from 'luxon';
import { ZodError } from 'zod';
import {
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import {
    zMarshalToBoolean,
    zMarshalToStrictInt,
    zMarshalToStrictIntNullable,
} from '../../ReachApi/Marshallers';

export enum CounterDateTypeEnum {
    DATE = 'date',
    DATE_TIME = 'dateTime',
    TIME = 'time',
}

export enum CounterDirectionEnum {
    DOWN = 'down',
    UP = 'up',
}

export enum CounterAmPmEnum {
    AM = 'AM',
    PM = 'PM',
}

export enum CounterTypeEnum {
    MANUAL = 'manual',
}

export const ZodCounterWidgetDate = z.object({
    /**
     * Day of month
     */
    date: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    month: z.string(),
    year: z.union([z.string(), z.number()]).nullish().transform(zMarshalToStrictIntNullable),
});

export const ZodCounterWidgetTime = z.object({
    ampm: z.string().transform((amPm) => {
        switch (amPm) {
            case CounterAmPmEnum.AM:
                return CounterAmPmEnum.AM;
            case CounterAmPmEnum.PM:
                return CounterAmPmEnum.PM;
            default:
                // The default is AM if the never set an ampm value,
                //  but are trying to display a time value.
                return CounterAmPmEnum.AM;
        }
    }),
    hour: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    minute: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
});

export const ZodCounterWidgetDateTime = ZodCounterWidgetDate.merge(ZodCounterWidgetTime);

/**
 * @example {"month":"Jan","date":"4","year":"2022"}
 */
export type CounterWidgetDate = z.infer<typeof ZodCounterWidgetDate>;
/**
 * @example {"month":"Nov","date":"2","year":"2019","hour":"02","minute":"00","ampm":"PM"}
 */
export type CounterWidgetDateTime = z.infer<typeof ZodCounterWidgetDateTime>;
/**
 * @example {"hour":"05","minute":"30","ampm":"PM"}
 */
export type CounterWidgetTime = z.infer<typeof ZodCounterWidgetTime>;

export const ZodCounterWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend({
    calendar_id: z.string(),
    counter_type: z.string(),
    data_list: z.string(),
    /**
     * ZodCounterWidgetDate | ZodCounterWidgetDateTime | ZodCounterWidgetTime,
     */
    date: z.string().nullish(),
    direction: z
        .string()
        .nullish()
        .transform((direction) => {
            switch (direction) {
                case CounterDirectionEnum.UP:
                    return CounterDirectionEnum.UP;
                case CounterDirectionEnum.DOWN:
                    return CounterDirectionEnum.DOWN;
                default:
                    throw new ZodError([
                        {
                            code: z.ZodIssueCode.invalid_type,
                            expected: 'string',
                            received: 'string',
                            path: [__fileline],
                            message: `${direction} is not a valid CounterDirectionEnum.`,
                        },
                    ]);
            }
        }),
    expiration_text: z.string(),
    // Ensure the handlebars is not empty
    handlebars: z.string().min(1),
    loop: z.unknown().transform(zMarshalToBoolean),
    timezone: z.string(),
    title: z.string(),
});

export type CounterWidgetConfigurationDetails = z.infer<
    typeof ZodCounterWidgetConfigurationDetails
>;

export const ZodCounterWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    config: ZodCounterWidgetConfigurationDetails,
    type: z.literal(WidgetType.COUNTER),
});

export type CounterWidgetConfiguration = z.infer<typeof ZodCounterWidgetConfiguration>;

export type CounterWidgetTemplateData = {
    counterText: string;
    Widget: { Id: number };
};

export type CounterWidgetTimeProperties = {
    title?: string;
    s?: number;
    ss?: string;
    m?: number;
    mm?: string;
    h?: number;
    hh?: string;
    d?: number;
    w?: number;
    mo?: number;
    y?: number;
    sl?: string;
    ml?: string;
    hl?: string;
    dl?: string;
    wl?: string;
    mol?: string;
    yl?: string;
};

export type CounterDateObject = {
    type: CounterDateTypeEnum;
    value: CounterWidgetDate | CounterWidgetDateTime | CounterWidgetTime;
    date: DateTime;
};

// TODO: Verify full shape is used...
export type CounterDateRowEntry = {
    date?: DateTime;
    title: string;
    type?: string;
    isCurrent?: boolean;
    fullDates?: {
        sdate: DateTime;
        edate: DateTime;
    };
};
